import React, { useEffect, useRef, useState } from 'react';
import './DevMode.css';

function DevMode({
  isOpen,
  onClose,
  tables,
  onTableUpdate,
  onTableCreate,
  onTableDelete,
  onSaveFloorPlanSnapshot,
  onRestoreFloorPlanSnapshot,
  onSaveNamedFloorPlan,
  onLoadNamedFloorPlan,
  onDeleteNamedFloorPlan,
  hasSavedFloorPlan,
  savedFloorPlans,
  selectedServiceLabel
}) {
  const DRAG_THRESHOLD = 8;
  const GRID_SIZE = 20;
  const [code, setCode] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [draggedTable, setDraggedTable] = useState(null);
  const [localTables, setLocalTables] = useState(tables);
  const [notice, setNotice] = useState('');
  const [pendingDeleteTable, setPendingDeleteTable] = useState(null);
  const [planName, setPlanName] = useState('');
  const [saveWithReservations, setSaveWithReservations] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const pressContextRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    setLocalTables(tables);
    setSelectedTable((currentSelectedTable) => {
      if (!currentSelectedTable) {
        return null;
      }

      const updated = tables.find((table) => table.id === currentSelectedTable.id);
      return updated || null;
    });
  }, [tables]);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }
    const timer = setTimeout(() => setNotice(''), 2200);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (!Array.isArray(savedFloorPlans) || savedFloorPlans.length === 0) {
      setSelectedPlanId('');
      return;
    }

    if (!savedFloorPlans.some((plan) => plan.id === selectedPlanId)) {
      setSelectedPlanId(savedFloorPlans[0].id);
    }
  }, [savedFloorPlans, selectedPlanId]);

  const snapToGrid = (value) => Math.round(value / GRID_SIZE) * GRID_SIZE;

  const getDragBounds = (table) => {
    if (!table) {
      return { width: 70, height: 70 };
    }

    if (table.type === 'round') {
      return { width: 70, height: 70 };
    }

    if (table.type === 'long') {
      return { width: 140, height: 70 };
    }

    if (table.type === 'rect-small') {
      return { width: 70, height: 50 };
    }

    return { width: 90, height: 70 };
  };

  const selectTable = (table) => {
    setSelectedTable(table);
    setLocalTables(prev => prev.map(t => t.id === table.id ? table : t));
  };

  const handleCodeSubmit = () => {
    if (code === '1414') {
      setAuthenticated(true);
      setCode('');
    } else {
      setNotice('Code incorrect');
      setCode('');
    }
  };

  const handleCreateTable = async () => {
    const newTable = await onTableCreate();
    if (newTable) {
      setLocalTables((prev) => (prev.some((t) => t.id === newTable.id) ? prev : [...prev, newTable]));
      setSelectedTable(newTable);
    }
  };

  const handleDeleteTable = async (tableId) => {
    const ok = await onTableDelete(tableId);
    if (!ok) {
      setNotice('Suppression impossible. Rechargez le plan et reessayez.');
      return;
    }

    setLocalTables((prev) => prev.filter((t) => t.id !== tableId));
    if (selectedTable?.id === tableId) {
      setSelectedTable(null);
    }
    setNotice('Table supprimee');
  };

  const requestDeleteTable = (table) => {
    setPendingDeleteTable(table);
  };

  const confirmDeleteTable = async () => {
    if (!pendingDeleteTable) {
      return;
    }
    await handleDeleteTable(pendingDeleteTable.id);
    setPendingDeleteTable(null);
  };

  const commitDragPosition = (table, nextX, nextY) => {
    const bounds = getDragBounds(table);
    const canvas = canvasRef.current;
    const maxX = canvas ? canvas.scrollWidth - bounds.width - 12 : 1200 - bounds.width - 12;
    const maxY = canvas ? canvas.scrollHeight - bounds.height - 12 : 700 - bounds.height - 12;
    const x = Math.max(0, Math.min(snapToGrid(nextX), Math.max(0, maxX)));
    const y = Math.max(0, Math.min(snapToGrid(nextY), Math.max(0, maxY)));
    return { x, y };
  };

  const handleMouseDown = (tableId, e) => {
    e.preventDefault();
    e.stopPropagation();
    const table = localTables.find((item) => item.id === tableId);
    const linkedTables = table?.groupId
      ? localTables.filter((item) => item.groupId === table.groupId)
      : (table ? [table] : []);
    const startPositions = linkedTables.reduce((acc, item) => {
      acc[item.id] = { x: item.x || 0, y: item.y || 0 };
      return acc;
    }, {});
    const canvas = canvasRef.current;
    const canvasRect = canvas ? canvas.getBoundingClientRect() : { left: 0, top: 0, scrollLeft: 0, scrollTop: 0 };
    const scrollLeft = canvas ? canvas.scrollLeft : 0;
    const scrollTop = canvas ? canvas.scrollTop : 0;

    pressContextRef.current = {
      tableId,
      startX: e.clientX,
      startY: e.clientY,
      dragStarted: false,
      longPressTriggered: false,
      startTableX: table?.x || 0,
      startTableY: table?.y || 0,
      linkedTableIds: linkedTables.map((item) => item.id),
      startPositions,
      startCanvasScrollLeft: scrollLeft,
      startCanvasScrollTop: scrollTop,
      canvasLeft: canvasRect.left,
      canvasTop: canvasRect.top
    };
  };

  const handleMouseMove = (e) => {
    if (!draggedTable && pressContextRef.current) {
      const dx = e.clientX - pressContextRef.current.startX;
      const dy = e.clientY - pressContextRef.current.startY;
      const moved = Math.sqrt(dx * dx + dy * dy);

      if (!pressContextRef.current.longPressTriggered && moved > DRAG_THRESHOLD) {
        pressContextRef.current.dragStarted = true;
        setDraggedTable(pressContextRef.current.tableId);
        const table = localTables.find((t) => t.id === pressContextRef.current.tableId);
        if (table) {
          selectTable(table);
        }
      }
    }

    if (!draggedTable) return;

    const canvasScroll = document.querySelector('.dev-canvas');
    if (!pressContextRef.current) return;

    const scrollLeft = canvasScroll ? canvasScroll.scrollLeft : 0;
    const scrollTop = canvasScroll ? canvasScroll.scrollTop : 0;
    const deltaX = (e.clientX - pressContextRef.current.startX) + (scrollLeft - pressContextRef.current.startCanvasScrollLeft);
    const deltaY = (e.clientY - pressContextRef.current.startY) + (scrollTop - pressContextRef.current.startCanvasScrollTop);
    const linkedTableIds = pressContextRef.current.linkedTableIds || [draggedTable];
    const updatesById = new Map();

    linkedTableIds.forEach((tableId) => {
      const table = localTables.find((item) => item.id === tableId);
      const startPos = pressContextRef.current.startPositions?.[tableId] || { x: table?.x || 0, y: table?.y || 0 };
      const { x, y } = commitDragPosition(
        table,
        startPos.x + deltaX,
        startPos.y + deltaY
      );
      updatesById.set(tableId, { x, y });
    });

    const updatedPrimary = updatesById.get(draggedTable);
    if (updatedPrimary) {
      const updatedTable = { ...(localTables.find((t) => t.id === draggedTable) || {}), ...updatedPrimary };
      setSelectedTable(updatedTable);
    }

    setLocalTables((prev) => prev.map((table) => {
      const next = updatesById.get(table.id);
      return next ? { ...table, x: next.x, y: next.y } : table;
    }));

    updatesById.forEach((coords, tableId) => {
      onTableUpdate(tableId, { x: Math.round(coords.x), y: Math.round(coords.y) });
    });
  };

  const handleMouseUp = () => {
    if (pressContextRef.current && !pressContextRef.current.dragStarted) {
      const clickedTable = localTables.find((table) => table.id === pressContextRef.current.tableId);
      if (clickedTable) {
        selectTable(clickedTable);
      }
    }

    pressContextRef.current = null;
    setDraggedTable(null);
  };

  const formatPlanDate = (value) => {
    if (!value) {
      return '';
    }
    try {
      return new Date(value).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return '';
    }
  };

  const handleSaveNamedPlan = async () => {
    const result = await onSaveNamedFloorPlan(planName, saveWithReservations);
    if (!result?.ok) {
      setNotice(result?.message || 'Impossible de sauvegarder ce plan.');
      return;
    }
    setNotice(`Plan "${result.preset.name}" sauvegarde`);
    setPlanName('');
    setSelectedPlanId(result.preset.id);
  };

  const handleLoadNamedPlan = async (planId) => {
    const ok = await onLoadNamedFloorPlan(planId);
    if (!ok) {
      setNotice('Chargement du plan impossible.');
      return;
    }
    setSelectedPlanId(planId);
    setNotice('Plan charge avec succes.');
  };

  const handleDeleteNamedPlan = async (planId) => {
    const ok = await onDeleteNamedFloorPlan(planId);
    if (!ok) {
      setNotice('Suppression du plan impossible.');
      return;
    }
    setNotice('Plan supprime.');
  };

  if (!isOpen) return null;

  return (
    <div className="dev-mode-overlay">
      <div className="dev-mode-container">
        {!authenticated ? (
          <div className="dev-auth">
            <h2>🔐 Mode Développement</h2>
            <p>Entrez le code d'accès:</p>
            <div className="auth-input-group">
              <input
                type="password"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCodeSubmit()}
                placeholder="Code secret"
                autoFocus
              />
              <button onClick={handleCodeSubmit}>Valider</button>
            </div>
            <button className="btn-close" onClick={onClose}>✕ Fermer</button>
          </div>
        ) : (
          <div className="dev-content">
            <div className="dev-header">
              <h2>🔧 Mode Développement - {selectedServiceLabel}</h2>
              <div className="dev-header-actions">
                <button className="btn-dev-header-action" onClick={onSaveFloorPlanSnapshot}>Sauver plan</button>
                <button
                  className="btn-dev-header-action secondary"
                  onClick={onRestoreFloorPlanSnapshot}
                  disabled={!hasSavedFloorPlan}
                >
                  Revenir a l'origine
                </button>
                <button className="btn-close" onClick={() => { setAuthenticated(false); onClose(); }}>✕ Fermer</button>
              </div>
            </div>

            {notice && <div className="dev-notice">{notice}</div>}

            <div className="dev-main">
              <div className="dev-left">
                <div className="dev-saved-plans">
                  <h3>Plans sauvegardes</h3>
                  <div className="dev-save-plan-form">
                    <input
                      type="text"
                      value={planName}
                      onChange={(e) => setPlanName(e.target.value)}
                      placeholder="Nom du plan"
                    />
                    <label className="dev-checkbox-row">
                      <input
                        type="checkbox"
                        checked={saveWithReservations}
                        onChange={(e) => setSaveWithReservations(e.target.checked)}
                      />
                      Sauver avec reservations actuelles
                    </label>
                    <button className="btn-dev-plan-save" onClick={handleSaveNamedPlan}>
                      Sauver ce plan
                    </button>
                  </div>

                  <div className="dev-saved-plan-list">
                    {!savedFloorPlans?.length && (
                      <p className="dev-saved-empty">Aucun plan sauvegarde pour ce service.</p>
                    )}

                    {savedFloorPlans?.map((plan) => (
                      <div
                        key={plan.id}
                        className={`dev-saved-plan-item ${selectedPlanId === plan.id ? 'selected' : ''}`}
                        onClick={() => setSelectedPlanId(plan.id)}
                      >
                        <div className="dev-saved-plan-name">{plan.name}</div>
                        <div className="dev-saved-plan-meta">
                          {plan.includeReservations ? 'Avec reservations' : 'Sans reservations'}
                          {plan.createdAt ? ` • ${formatPlanDate(plan.createdAt)}` : ''}
                        </div>
                        <div className="dev-saved-plan-actions">
                          <button
                            className="btn-dev-plan-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLoadNamedPlan(plan.id);
                            }}
                          >
                            Charger
                          </button>
                          <button
                            className="btn-dev-plan-action danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNamedPlan(plan.id);
                            }}
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="dev-table-header">
                  <h3>Tables</h3>
                  <button className="btn-create" onClick={handleCreateTable}>➕</button>
                </div>
                <div className="dev-table-list">
                  {localTables.map(table => (
                    <button
                      key={table.id}
                      className={`dev-table-item ${selectedTable?.id === table.id ? 'selected' : ''}`}
                      onClick={() => selectTable(table)}
                    >
                      Table #{table.number} ({table.capacity}P){table.groupId ? ' [G]' : ''}
                    </button>
                  ))}
                </div>
              </div>

              <div className="dev-right">
                {selectedTable ? (
                  <DevTableEditor 
                    table={selectedTable} 
                    onUpdate={onTableUpdate}
                    onTableChange={selectTable}
                    onDeleteRequest={requestDeleteTable}
                    onNotify={setNotice}
                  />
                ) : (
                  <div className="dev-placeholder">
                    <p>Sélectionnez une table pour éditer ses paramètres</p>
                  </div>
                )}
              </div>
            </div>

            <div className="dev-canvas-container">
              <div className="dev-canvas-header">
                <h3>Plan de salle</h3>
                <div className="dev-canvas-meta">
                  <span className="dev-chip">Snap {GRID_SIZE}px</span>
                  <span className="dev-chip">Déplacement libre au drag</span>
                </div>
              </div>
              <p className="dev-hint">Clique une table pour afficher ses paramètres, ou glisse-la pour la repositionner. Les positions s'alignent automatiquement sur la grille.</p>
              <div
                className="dev-canvas"
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                ref={canvasRef}
              >
                <div className="dev-canvas-inner">
                  <div className="dev-grid-overlay" aria-hidden="true"></div>
                  {localTables.map(table => {
                    const currentTable = selectedTable?.id === table.id ? selectedTable : table;
                    const isDraggedInGroup = Boolean(
                      draggedTable
                      && pressContextRef.current?.linkedTableIds?.includes(table.id)
                    );
                    return (
                      <div
                        key={table.id}
                        className={`dev-table-drag table-${table.type} ${table.groupId ? 'grouped' : ''}`}
                        style={{ 
                          left: `${currentTable.x}px`, 
                          top: `${currentTable.y}px`,
                          cursor: isDraggedInGroup ? 'grabbing' : 'grab'
                        }}
                        onMouseDown={(e) => handleMouseDown(table.id, e)}
                      >
                        #{table.number}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {pendingDeleteTable && (
          <div className="dev-dialog-overlay" onClick={() => setPendingDeleteTable(null)}>
            <div className="dev-dialog" onClick={(e) => e.stopPropagation()}>
              <h3>Confirmer suppression</h3>
              <p>Voulez-vous supprimer la table #{pendingDeleteTable.number} ?</p>
              <div className="dev-dialog-actions">
                <button className="btn-dev-dialog cancel" onClick={() => setPendingDeleteTable(null)}>Non</button>
                <button className="btn-dev-dialog confirm" onClick={confirmDeleteTable}>Oui</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DevTableEditor({ table, onUpdate, onTableChange, onDeleteRequest, onNotify }) {
  const [formData, setFormData] = useState(table);

  useEffect(() => {
    setFormData(table);
  }, [table]);

  const handleChange = async (field, value) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    onTableChange(updatedData);

    if (['number', 'capacity', 'type', 'x', 'y'].includes(field)) {
      const normalized = {
        ...updatedData,
        number: parseInt(updatedData.number, 10),
        capacity: parseInt(updatedData.capacity, 10),
        x: parseInt(updatedData.x, 10),
        y: parseInt(updatedData.y, 10)
      };

      if (
        !Number.isNaN(normalized.number)
        && normalized.number > 0
        && !Number.isNaN(normalized.capacity)
        && normalized.capacity >= 2
        && normalized.capacity <= 8
        && !Number.isNaN(normalized.x)
        && !Number.isNaN(normalized.y)
      ) {
        const saved = await onUpdate(table.id, normalized);
        if (saved) {
          setFormData(saved);
          onTableChange(saved);
        }
      }
    }
  };

  const normalizeForSave = () => ({
    ...formData,
    number: parseInt(formData.number, 10),
    capacity: parseInt(formData.capacity, 10),
    x: parseInt(formData.x, 10),
    y: parseInt(formData.y, 10)
  });

  const handleSave = async () => {
    const normalized = normalizeForSave();
    if (Number.isNaN(normalized.number) || normalized.number <= 0) {
      onNotify('Le numero de table doit etre valide.');
      return;
    }

    const updated = await onUpdate(table.id, normalized);
    if (updated) {
      setFormData(updated);
      onTableChange(updated);
    }
    onNotify('Table mise a jour');
  };

  return (
    <div className="dev-editor">
      <div className="dev-editor-title">
        <div>
          <p className="dev-editor-kicker">Édition rapide</p>
          <h3>Table #{table.number}</h3>
        </div>
        <div className="dev-editor-badge">{table.capacity} places</div>
      </div>

      <div className="dev-form-group">
        <label>Numéro de table:</label>
        <input
          type="text"
          inputMode="numeric"
          value={formData.number}
          onChange={(e) => handleChange('number', e.target.value.replace(/[^0-9]/g, ''))}
          placeholder="Ex: 24"
        />
      </div>

      <div className="dev-form-group">
        <label>Capacité:</label>
        <input
          type="number"
          min="2"
          max="8"
          value={formData.capacity}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10);
            if (!Number.isNaN(value) && value >= 2 && value <= 8) {
              handleChange('capacity', value);
            }
          }}
        />
      </div>

      <div className="dev-form-group">
        <label>Type de table:</label>
        <select value={formData.type} onChange={(e) => handleChange('type', e.target.value)}>
          <option value="round">Ronde</option>
          <option value="rect">Rectangle normal</option>
          <option value="rect-small">Rectangle petit</option>
          <option value="long">Longue</option>
        </select>
      </div>

      <div className="dev-form-group">
        <label>Position X: {formData.x}px</label>
        <input
          type="range"
          min="0"
          max="1600"
          value={formData.x}
          onChange={(e) => handleChange('x', parseInt(e.target.value))}
          className="range-slider"
        />
      </div>

      <div className="dev-form-group">
        <label>Position Y: {formData.y}px</label>
        <input
          type="range"
          min="0"
          max="900"
          value={formData.y}
          onChange={(e) => handleChange('y', parseInt(e.target.value))}
          className="range-slider"
        />
      </div>

      <button className="btn-save" onClick={handleSave}>💾 Enregistrer</button>
      <button className="btn-delete" onClick={() => onDeleteRequest(table)}>🗑️ Supprimer</button>
    </div>
  );
}

export default DevMode;
